import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MousePointer2, Pen, Square, Circle, Type, Undo2, Trash2, Save } from "lucide-react";

const TOOLS = [
  { id: "arrow", label: "Arrow", icon: MousePointer2 },
  { id: "pen", label: "Pen", icon: Pen },
  { id: "rect", label: "Box", icon: Square },
  { id: "ellipse", label: "Circle", icon: Circle },
  { id: "text", label: "Note", icon: Type },
];
const COLORS = ["#dc2626", "#facc15", "#22c55e", "#3b82f6", "#ffffff"];

const draw = (ctx, s, w, h) => {
  const x = (v) => v * w;
  const y = (v) => v * h;
  ctx.strokeStyle = s.color;
  ctx.fillStyle = s.color;
  ctx.lineWidth = Math.max(2, Math.round(w / 320));
  ctx.lineCap = "round";
  if (s.tool === "pen") {
    ctx.beginPath();
    s.points.forEach((p, i) => (i ? ctx.lineTo(x(p[0]), y(p[1])) : ctx.moveTo(x(p[0]), y(p[1]))));
    ctx.stroke();
  } else if (s.tool === "rect") {
    ctx.strokeRect(x(s.x1), y(s.y1), x(s.x2 - s.x1), y(s.y2 - s.y1));
  } else if (s.tool === "ellipse") {
    ctx.beginPath();
    ctx.ellipse((x(s.x1) + x(s.x2)) / 2, (y(s.y1) + y(s.y2)) / 2,
      Math.abs(x(s.x2 - s.x1)) / 2, Math.abs(y(s.y2 - s.y1)) / 2, 0, 0, Math.PI * 2);
    ctx.stroke();
  } else if (s.tool === "arrow") {
    const [ax, ay, bx, by] = [x(s.x1), y(s.y1), x(s.x2), y(s.y2)];
    ctx.beginPath();
    ctx.moveTo(ax, ay); ctx.lineTo(bx, by); ctx.stroke();
    const ang = Math.atan2(by - ay, bx - ax);
    const head = Math.max(10, w / 42);
    ctx.beginPath();
    ctx.moveTo(bx, by);
    ctx.lineTo(bx - head * Math.cos(ang - Math.PI / 7), by - head * Math.sin(ang - Math.PI / 7));
    ctx.lineTo(bx - head * Math.cos(ang + Math.PI / 7), by - head * Math.sin(ang + Math.PI / 7));
    ctx.closePath();
    ctx.fill();
  } else if (s.tool === "text") {
    const size = Math.max(14, Math.round(w / 34));
    ctx.font = `600 ${size}px Inter, system-ui, sans-serif`;
    ctx.textBaseline = "top";
    const pad = size * 0.3;
    const tw = ctx.measureText(s.text).width;
    ctx.fillStyle = "rgba(15,23,42,0.72)";
    ctx.fillRect(x(s.x1) - pad, y(s.y1) - pad, tw + pad * 2, size + pad * 2);
    ctx.fillStyle = s.color;
    ctx.fillText(s.text, x(s.x1), y(s.y1));
  }
};

export const ImageAnnotator = ({ src, title, initialShapes = [], onSave, onClose }) => {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const [shapes, setShapes] = useState(initialShapes);
  const [tool, setTool] = useState("arrow");
  const [color, setColor] = useState("#dc2626");
  const [drafting, setDrafting] = useState(null);
  const [ready, setReady] = useState(false);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      setReady(true);
    };
    img.src = src;
  }, [src]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!ready || !canvas || !img) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    [...shapes, drafting].filter(Boolean).forEach((s) => draw(ctx, s, canvas.width, canvas.height));
  }, [shapes, drafting, ready]);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return [(p.clientX - r.left) / r.width, (p.clientY - r.top) / r.height];
  };

  const start = (e) => {
    const [x, y] = pos(e);
    if (tool === "text") {
      if (!note.trim()) return;
      setShapes((s) => [...s, { tool: "text", x1: x, y1: y, text: note.trim(), color }]);
      setNote("");
      return;
    }
    if (tool === "pen") setDrafting({ tool: "pen", points: [[x, y]], color });
    else setDrafting({ tool, x1: x, y1: y, x2: x, y2: y, color });
  };

  const move = (e) => {
    if (!drafting) return;
    const [x, y] = pos(e);
    setDrafting((d) => (d.tool === "pen" ? { ...d, points: [...d.points, [x, y]] } : { ...d, x2: x, y2: y }));
  };

  const end = () => {
    if (!drafting) return;
    setShapes((s) => [...s, drafting]);
    setDrafting(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      await onSave(shapes, canvasRef.current.toDataURL("image/png"));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[95vh] max-w-5xl overflow-y-auto" data-testid="image-annotator">
        <DialogHeader><DialogTitle className="font-display">Annotate — {title}</DialogTitle></DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          {TOOLS.map((t) => (
            <Button key={t.id} type="button" size="sm" variant={tool === t.id ? "default" : "outline"}
              onClick={() => setTool(t.id)} data-testid={`annot-tool-${t.id}`}
              className={`h-10 ${tool === t.id ? "bg-[#134e4a] hover:bg-[#0f3f3c]" : ""}`}>
              <t.icon size={14} className="mr-1" /> {t.label}
            </Button>
          ))}
          <div className="ml-1 flex items-center gap-1.5">
            {COLORS.map((c) => (
              <button key={c} type="button" onClick={() => setColor(c)} aria-label={`Colour ${c}`}
                data-testid={`annot-color-${c.replace("#", "")}`}
                className={`h-7 w-7 rounded-full border-2 ${color === c ? "border-slate-900" : "border-slate-300"}`}
                style={{ backgroundColor: c }} />
            ))}
          </div>
          <Button type="button" size="sm" variant="outline" onClick={() => setShapes((s) => s.slice(0, -1))}
            disabled={!shapes.length} data-testid="annot-undo" className="h-10"><Undo2 size={14} className="mr-1" /> Undo</Button>
          <Button type="button" size="sm" variant="outline" onClick={() => setShapes([])}
            disabled={!shapes.length} data-testid="annot-clear" className="h-10"><Trash2 size={14} className="mr-1" /> Clear</Button>
          <Button type="button" size="sm" onClick={save} disabled={saving} data-testid="annot-save"
            className="ml-auto h-10 bg-[#134e4a] hover:bg-[#0f3f3c]"><Save size={14} className="mr-1" />{saving ? "Saving…" : "Save marks"}</Button>
        </div>

        {tool === "text" && (
          <Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="annot-text-input"
            placeholder="Type the note, then tap the spot on the photo" className="h-11 border-slate-300" />
        )}

        <div className="border border-slate-200 bg-slate-900/5 p-2">
          <canvas
            ref={canvasRef}
            data-testid="annot-canvas"
            className="mx-auto block max-h-[62vh] w-full cursor-crosshair touch-none object-contain"
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          />
        </div>
        <p className="text-xs text-slate-500">
          The original photo is never changed — marks are stored separately and stay editable. Saving also keeps a flattened copy for reports.
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default ImageAnnotator;
